/**
 * GrantsModal — Task 26 (ZTNA Customers v2)
 *
 * Modal para adicionar 1+ grants (aplicacoes) a um cliente existente.
 *
 *  - Carrega lista de apps grantable via API.
 *  - Exclui apps que o cliente ja tem grant ativo (nao revogado).
 *  - Agrupa MultiSelect por "tenant / environment".
 *  - Expiracao max = hoje + 365 dias.
 *  - Tratamento explicito de 409 (ja existe) e 403 (sem acesso).
 *
 * Spec §10.2.
 */
import { useMemo, useState } from 'react'
import {
  Button,
  Group,
  Modal,
  MultiSelect,
  Stack,
} from '@mantine/core'
import { DatePickerInput } from '@mantine/dates'
import { notifications } from '@mantine/notifications'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'

import {
  ztnaCustomersApi,
  type AddGrantsBody,
  type ZtnaCustomer,
} from '@/api/ztnaCustomers'
import { formatApiError } from '@/api/errors'

interface Props {
  customer: ZtnaCustomer
  opened: boolean
  onClose: () => void
}

export function GrantsModal({ customer, opened, onClose }: Props) {
  const queryClient = useQueryClient()

  const { data: apps = [] } = useQuery({
    queryKey: ['ztna', 'grantable-apps'],
    queryFn: () => ztnaCustomersApi.listGrantableApps(),
    enabled: opened,
  })

  // Exclui apps que ja tem grant ativo (nao revogado)
  const existingAppIds = new Set(
    customer.grants.filter((g) => !g.revoked_at).map((g) => g.application_id),
  )
  const available = apps.filter((a) => !existingAppIds.has(a.id))

  const grouped = useMemo(() => {
    const m = new Map<string, { value: string; label: string }[]>()
    for (const a of available) {
      const g = `${a.tenant_name} / ${a.environment}`
      const item = {
        value: String(a.id),
        label: `${a.display_name} — ${a.hostname}`,
      }
      if (!m.has(g)) m.set(g, [])
      m.get(g)!.push(item)
    }
    return Array.from(m.entries()).map(([group, items]) => ({ group, items }))
  }, [available])

  const [selected, setSelected] = useState<string[]>([])
  const [expiresAt, setExpiresAt] = useState<Date | null>(null)

  const mut = useMutation({
    mutationFn: () => {
      const body: AddGrantsBody = {
        application_ids: selected.map(Number),
        expires_at: dayjs(expiresAt!).utc().toISOString(),
      }
      return ztnaCustomersApi.addGrants(customer.id, body)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ztna', 'customers'] })
      queryClient.invalidateQueries({
        queryKey: ['ztna', 'customer', customer.id],
      })
      notifications.show({
        title: 'Acessos adicionados',
        message: 'Cliente notificado por e-mail.',
        color: 'teal',
      })
      setSelected([])
      setExpiresAt(null)
      onClose()
    },
    onError: (err: any) => {
      const status = err.response?.status
      if (status === 409) {
        notifications.show({
          title: 'Já existe',
          message:
            'Cliente já tem grant ativo pra uma das apps. Use Renovar.',
          color: 'orange',
        })
      } else if (status === 403) {
        notifications.show({
          title: 'Permissão negada',
          message: 'Você não administra uma das apps selecionadas.',
          color: 'red',
        })
      } else {
        notifications.show({
          title: 'Erro',
          message: formatApiError(err, 'Não foi possível salvar as permissões.'),
          color: 'red',
        })
      }
    },
  })

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Adicionar aplicações"
      size="lg"
    >
      <Stack>
        <MultiSelect
          data={grouped}
          value={selected}
          onChange={setSelected}
          label="Aplicações disponíveis"
          description={`${available.length} app(s) disponíveis (excluindo as já autorizadas)`}
          searchable
          clearable
          withAsterisk
          placeholder="Selecione…"
        />
        <DatePickerInput
          label="Expiração"
          value={expiresAt}
          onChange={setExpiresAt}
          minDate={new Date()}
          maxDate={dayjs().add(365, 'day').toDate()}
          valueFormat="YYYY-MM-DD"
          withAsterisk
        />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            color="teal"
            disabled={selected.length === 0 || !expiresAt}
            loading={mut.isPending}
            onClick={() => mut.mutate()}
          >
            Adicionar
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
}

export default GrantsModal
